import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  Ticket, Power, Gift, Plus, Copy, Pencil, Trash2, CalendarX, Dices,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { scopeCoursesToUser } from '../../utils/scope';
import {
  getDiscounts, createDiscount, updateDiscount, toggleDiscount, deleteDiscount,
} from '../../api/discounts';
import { getCoursesAdmin } from '../../api/courses';
import { useAuth } from '../../context/AuthContext';
import { unwrapList } from '../../utils/api';
import PageHead from '../../components/PageHead/PageHead';
import SearchInput from '../../components/SearchInput/SearchInput';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Dropdown from '../../components/Dropdown/Dropdown';
import DatePicker from '../../components/DatePicker/DatePicker';
import { shiftYmd } from '../../components/DatePicker/DateRangePicker';
import MetricCard from '../../components/MetricCard/MetricCard';
import SubTabs from '../../components/SubTabs/SubTabs';
import Modal from '../../components/Modal/Modal';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import {
  formatDate, formatDateRange, formatMoney, formatNumber, toYmd, todayYmd,
} from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { useScrollFade } from '../../hooks/useScrollFade';
import './Promocodes.css';

const EMPTY = {
  name: '', code: '', type: 'PERCENTAGE', value: '',
  scope: 'GLOBAL', courseIds: [],
  startsAt: '', endsAt: '', maxUses: '', maxUsesPerUser: '', minOrderAmount: '', isActive: true,
};

// A promocode runs on Tashkent days: from 00:00 of its first day to 23:59:59 of its last, whatever
// time zone the staff member's laptop is in (Uzbekistan is UTC+5 all year, no daylight saving).
// The API stores an instant, so a date the person picked ("31 August") is sent as the end of that
// day: a bare "2025-08-31" is midnight at the START of the 31st, which expired the code a day early.
const ZONE = { timeZone: 'Asia/Tashkent' };
const OFFSET = '+05:00';
const startInstant = (ymd) => `${ymd}T00:00:00.000${OFFSET}`;
const endInstant = (ymd) => `${ymd}T23:59:59.999${OFFSET}`;
const toPickerDate = (v) => (v ? (toYmd(v, ZONE) || '') : '');

// Quick lengths for the validity: "valid for 30 days" counts the first day, as the pickers' "last 7 days" does
const DURATIONS = [7, 30, 90];

// The list is read 100 codes at a time until the server says there are no more (a tenant with
// more than a thousand codes is not a case this page plans for).
const PAGE_LIMIT = 100;
const MAX_PAGES = 10;
async function fetchAllDiscounts() {
  const all = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await getDiscounts({ limit: PAGE_LIMIT, page });
    all.push(...unwrapList(res));
    if (!res?.meta?.hasNext) break;
  }
  return all;
}

// Which complaint a field belongs to, and which element takes the focus when it has one
const FIELD_ERROR = {
  code: 'code', type: 'value', value: 'value', scope: 'scope', courseIds: 'scope',
  startsAt: 'dates', endsAt: 'dates', maxUses: 'limits', maxUsesPerUser: 'limits',
};
const ERROR_TARGET = { code: 'pc-code', value: 'pc-value', scope: 'pc-courses', dates: 'pc-starts', limits: 'pc-max' };

function genCode(len = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const arr = new Uint32Array(len);
  const rng = typeof window !== 'undefined' ? (window.crypto || window.msCrypto) : null;
  if (rng?.getRandomValues) rng.getRandomValues(arr);
  else for (let i = 0; i < len; i++) arr[i] = Math.floor(Math.random() * 0xffffffff);
  let out = '';
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

// Where a code stands right now, in one word. A code that is switched off never reads "Active"
// (the switch and the badge would disagree); an expired one still says it expired. `group` sorts
// the codes into the three filters: working now, expired, and everything else that is not working
// (switched off, not started yet, used up).
function expiryState(pc, now) {
  if (pc.endsAt && new Date(pc.endsAt).getTime() < now) return { key: 'statusExpired', tone: 'danger', group: 'expired' };
  if (!pc.isActive) return { key: 'statusInactive', tone: 'neutral', group: 'inactive' };
  if (pc.startsAt && new Date(pc.startsAt).getTime() > now) return { key: 'statusScheduled', tone: 'info', group: 'inactive' };
  if (pc.maxUses && (pc.usedCount || 0) >= pc.maxUses) return { key: 'statusUsedUp', tone: 'warning', group: 'inactive' };
  return { key: 'statusActive', tone: 'success', group: 'active' };
}

// "1-noyabr – 1-dekabr 2026" may wrap at the dash, never inside a date ("1-" / "dekabr")
function DateRange({ text }) {
  const parts = text.split(' – ');
  if (parts.length !== 2) return text;
  return (
    <>
      <span className="pc-nowrap">{`${parts[0]} –`}</span>
      {' '}
      <span className="pc-nowrap">{parts[1]}</span>
    </>
  );
}

// navigator.clipboard needs a secure page; the CRM may be opened from plain http on an office network.
async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const box = document.createElement('textarea');
  box.value = text;
  box.setAttribute('readonly', '');
  box.style.position = 'fixed';
  box.style.opacity = '0';
  document.body.appendChild(box);
  box.select();
  const ok = document.execCommand('copy');
  box.remove();
  if (!ok) throw new Error('copy failed');
}

function Promocodes() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modal, setModal] = useState(null); // { mode:'create'|'edit', form, initial, id }
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({}); // what is wrong with which field: { code, value, scope, dates, limits }
  const [formErr, setFormErr] = useState(null); // the save itself failed
  const formErrRef = useRef(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      // Promocodes = discounts that carry a code.
      setItems((await fetchAllDiscounts()).filter((d) => d.code));
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    getCoursesAdmin({ limit: 100, sortBy: 'title', sortOrder: 'asc' })
      .then((res) => {
        if (cancelled) return;
        const mine = scopeCoursesToUser(unwrapList(res), user);
        setCourses(mine.map((c) => ({ id: c.id, title: c.title })));
      })
      .catch(() => { /* non-fatal — scope picker just shows no courses */ });
    return () => { cancelled = true; };
  }, [user]);

  const courseTitle = useCallback((id) => courses.find((c) => c.id === id)?.title || t('promo.courseFallback'), [courses, t]);

  // Every figure on the page (the tiles, the filter counts, the rows) comes from this one list,
  // so they cannot disagree: "Faol" is what is working NOW, not what is switched on.
  const rows = useMemo(() => {
    const now = Date.now();
    return items.map((pc) => ({ pc, state: expiryState(pc, now) }));
  }, [items]);
  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.state.group === 'active').length,
    expired: rows.filter((r) => r.state.group === 'expired').length,
    inactive: rows.filter((r) => r.state.group === 'inactive').length,
  }), [rows]);
  const totalUses = useMemo(() => items.reduce((sum, p) => sum + (p.usedCount || 0), 0), [items]);

  const filtered = useMemo(() => rows.filter(({ pc, state }) => {
    if (statusFilter !== 'all' && state.group !== statusFilter) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      if (q && !(pc.code || '').toLowerCase().includes(q) && !(pc.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [rows, statusFilter, search]);

  const scroller = useScrollFade({ watch: `${filtered.length}-${loading}` });

  const statusChips = useMemo(() => [
    { key: 'all', label: t('promo.filterAll'), count: counts.all },
    { key: 'active', label: t('promo.filterActive'), count: counts.active },
    { key: 'expired', label: t('promo.statusExpired'), count: counts.expired },
    { key: 'inactive', label: t('promo.filterInactive'), count: counts.inactive },
  ], [t, counts]);

  const openCreate = () => {
    setFormErr(null);
    setErrors({});
    const form = { ...EMPTY, code: genCode() };
    setModal({ mode: 'create', form, initial: JSON.stringify(form) });
  };
  const openEdit = (pc) => {
    setFormErr(null);
    setErrors({});
    const form = {
      name: pc.name || '', code: pc.code || '', type: pc.type || 'PERCENTAGE', value: String(pc.value ?? ''),
      scope: pc.scope === 'COURSE' ? 'COURSE' : 'GLOBAL',
      courseIds: Array.isArray(pc.courseIds) ? pc.courseIds : [],
      startsAt: toPickerDate(pc.startsAt), endsAt: toPickerDate(pc.endsAt),
      maxUses: pc.maxUses != null ? String(pc.maxUses) : '',
      maxUsesPerUser: pc.maxUsesPerUser != null ? String(pc.maxUsesPerUser) : '',
      minOrderAmount: pc.minOrderAmount != null ? String(pc.minOrderAmount) : '',
      isActive: pc.isActive ?? true,
    };
    setModal({ mode: 'edit', id: pc.id, form, initial: JSON.stringify(form) });
  };
  const setField = (k, v) => {
    setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));
    // the person is fixing this field: its complaint goes away
    const group = FIELD_ERROR[k];
    if (group) setErrors((e) => (e[group] ? { ...e, [group]: undefined } : e));
  };
  const toggleCourse = (id) => {
    setModal((m) => {
      const has = m.form.courseIds.includes(id);
      return { ...m, form: { ...m.form, courseIds: has ? m.form.courseIds.filter((x) => x !== id) : [...m.form.courseIds, id] } };
    });
    setErrors((e) => (e.scope ? { ...e, scope: undefined } : e));
  };
  // "30 kun": valid from the start day (today when none is picked) for that many days, the first one included
  const applyDuration = (days) => {
    setModal((m) => {
      const start = m.form.startsAt || todayYmd(ZONE);
      return { ...m, form: { ...m.form, startsAt: start, endsAt: shiftYmd(start, days - 1) } };
    });
    setErrors((e) => (e.dates ? { ...e, dates: undefined } : e));
  };
  // Typed data is not thrown away by a stray click on the dimmed page behind the dialog.
  const dirty = Boolean(modal) && JSON.stringify(modal.form) !== modal.initial;

  // Every problem with the form at once, each on its own field, instead of one banner at the bottom
  const validate = (f) => {
    const found = {};
    if (!f.code.trim()) found.code = t('promo.errCodeRequired');
    if (!f.value || Number.isNaN(Number(f.value))) found.value = t('promo.errValidValue');
    else if (f.type === 'PERCENTAGE' && (Number(f.value) < 0 || Number(f.value) > 100)) found.value = t('promo.errPercentRange');
    if (f.scope === 'COURSE' && f.courseIds.length === 0) found.scope = t('promo.errPickCourse');
    // Both are "YYYY-MM-DD": they sort as text
    if (f.startsAt && f.endsAt && f.endsAt < f.startsAt) found.dates = t('promo.errEndAfterStart');
    const badLimit = (v) => v !== '' && (!Number.isInteger(Number(v)) || Number(v) < 1);
    if (badLimit(f.maxUses) || badLimit(f.maxUsesPerUser)) found.limits = t('promo.errLimits');
    return found;
  };

  const handleSave = async () => {
    const f = modal.form;
    const found = validate(f);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setFormErr(null);
      // the first field with a problem takes the focus, so the person sees where to look
      const first = ['code', 'value', 'scope', 'dates', 'limits'].find((k) => found[k]);
      const target = document.getElementById(ERROR_TARGET[first]);
      if (target) {
        target.focus({ preventScroll: true });
        target.scrollIntoView?.({ block: 'center' });
      }
      return;
    }
    setErrors({});

    setSaving(true);
    setFormErr(null);
    const code = f.code.trim().toUpperCase();
    // A blank optional field is left out when creating, but sent as null when editing: that is how
    // the API clears a value (leaving it out would keep the old end date / limit).
    const edit = modal.mode === 'edit';
    const cleared = edit ? null : undefined;
    const payload = {
      name: f.name.trim() || t('promo.defaultName', { code }),
      code,
      type: f.type,
      value: Number(f.value),
      scope: f.scope,
      courseIds: f.scope === 'COURSE' ? f.courseIds : (edit ? [] : undefined),
      startsAt: f.startsAt ? startInstant(f.startsAt) : cleared,
      endsAt: f.endsAt ? endInstant(f.endsAt) : cleared,
      maxUses: f.maxUses ? Number(f.maxUses) : cleared,
      maxUsesPerUser: f.maxUsesPerUser ? Number(f.maxUsesPerUser) : cleared,
      minOrderAmount: f.minOrderAmount ? Number(f.minOrderAmount) : cleared,
      isActive: f.isActive,
    };
    try {
      if (edit) { await updateDiscount(modal.id, payload); toast.success(t('promo.toastUpdated')); }
      else { await createDiscount(payload); toast.success(t('promo.toastCreated')); }
      setModal(null);
      await load();
    } catch {
      // Never the server's own text: it is English and says nothing the user can act on.
      setFormErr(t('promo.errSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // The banner sits at the end of a form that may be longer than the dialog: bring it into view
  useEffect(() => {
    if (formErr) formErrRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [formErr]);

  const handleToggle = async (pc) => {
    // Optimistic
    setItems((prev) => prev.map((x) => (x.id === pc.id ? { ...x, isActive: !x.isActive } : x)));
    try { await toggleDiscount(pc.id); }
    catch { setItems((prev) => prev.map((x) => (x.id === pc.id ? { ...x, isActive: pc.isActive } : x))); toast.error(t('promo.toastStatusFailed')); }
  };

  const handleDelete = async () => {
    const pc = confirmDel;
    setDeleting(true);
    try { await deleteDiscount(pc.id); toast.success(t('promo.toastDeleted')); setConfirmDel(null); await load(); }
    catch { toast.error(t('promo.toastDeleteFailed')); }
    finally { setDeleting(false); }
  };

  const copyCode = async (code) => {
    try {
      await copyText(code);
      toast.success(t('promo.toastCopied', { code }));
    } catch {
      toast.error(t('promo.toastCopyFailed'));
    }
  };

  const money = (n) => formatMoney(n, 'UZS', { lang, free: false });
  const discountLabel = (pc) => (pc.type === 'PERCENTAGE'
    ? t('promo.percentOff', { value: formatNumber(Number(pc.value), { lang }) })
    : t('promo.amountOff', { amount: money(pc.value) }));

  // "1-iyun – 31-avgust 2025", "1-iyun dan", "31-avgust gacha" or "Muddatsiz"
  const validityLabel = (pc) => {
    if (pc.startsAt && pc.endsAt) return formatDateRange(pc.startsAt, pc.endsAt, { lang, ...ZONE });
    if (pc.startsAt) return t('promo.validFrom', { date: formatDate(pc.startsAt, { lang, ...ZONE }) });
    if (pc.endsAt) return t('promo.validUntil', { date: formatDate(pc.endsAt, { lang, ...ZONE }) });
    return t('promo.validOpen');
  };

  // "Barcha kurslar" · the course's own name · "3 ta kurs" (the names of all of them on hover)
  const isCourseScoped = (pc) => pc.scope === 'COURSE' && Array.isArray(pc.courseIds) && pc.courseIds.length > 0;
  const scopeLabel = (pc) => {
    if (!isCourseScoped(pc)) return t('promo.allCoursesTitle');
    return pc.courseIds.length === 1 ? courseTitle(pc.courseIds[0]) : t('promo.coursesCount', { count: pc.courseIds.length });
  };
  const scopeTitle = (pc) => (isCourseScoped(pc) ? pc.courseIds.map(courseTitle).join(', ') : undefined);

  const resetFilters = () => { setSearch(''); setStatusFilter('all'); };
  const form = modal?.form;
  // Four zeros above "no promocodes yet" say it twice; a failed load must not claim zeros at all
  const showTiles = !loadFailed && (loading || items.length > 0);

  return (
    <div className="pc-page">
      <PageHead
        icon={<Ticket />}
        title={t('promo.title')}
        description={t('promo.subtitle')}
        actions={(
          <button type="button" className="ds-btn ds-btn-primary" onClick={openCreate}>
            <Plus aria-hidden="true" />
            {t('promo.newPromocode')}
          </button>
        )}
      />

      <div className="ui-stack pc-wrap">
        {/* KPI tiles: zeros would claim the list is empty when it simply failed to load */}
        {showTiles && (
          <div className="kpi-row">
            <MetricCard
              title={t('promo.statTotalCodes')}
              value={formatNumber(counts.all, { lang })}
              icon={<Ticket size={20} />}
              tone="brand"
              loading={loading}
            />
            <MetricCard
              title={t('promo.statActiveLabel')}
              value={formatNumber(counts.active, { lang })}
              icon={<Power size={20} />}
              tone="success"
              loading={loading}
            />
            <MetricCard
              title={t('promo.statusExpired')}
              value={formatNumber(counts.expired, { lang })}
              icon={<CalendarX size={20} />}
              tone="coral"
              loading={loading}
            />
            <MetricCard
              title={t('promo.statTotalRedemptions')}
              value={formatNumber(totalUses, { lang })}
              icon={<Gift size={20} />}
              tone="purple"
              loading={loading}
            />
          </div>
        )}

        {loading ? (
          <LoadingState skeleton="table" rows={5} message={t('promo.loading')} />
        ) : loadFailed ? (
          <PageEmptyState variant="error" size="lg" onRetry={load} />
        ) : items.length === 0 ? (
          <PageEmptyState
            variant="revenue"
            title={t('promo.emptyTitleNone')}
            description={t('promo.emptyDescNone')}
            actionLabel={t('promo.newPromocode')}
            onAction={openCreate}
          />
        ) : (
          <section className="ui-table-card">
            <div className="pc-toolbar">
              <SearchInput
                className="pc-search"
                value={search}
                onChange={setSearch}
                placeholder={t('promo.searchPlaceholder')}
              />
              <div className="ui-chip-row pc-filters" role="group" aria-label={t('promo.filterStatusAria')}>
                {statusChips.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`ui-chip${statusFilter === f.key ? ' is-active' : ''}`}
                    aria-pressed={statusFilter === f.key}
                    onClick={() => setStatusFilter(f.key)}
                  >
                    {f.label}
                    <span className="ui-count">{formatNumber(f.count, { lang })}</span>
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="pc-empty">
                <InlineEmpty
                  kind="search"
                  title={t('promo.emptyTitleFiltered')}
                  description={t('promo.emptyDescFiltered')}
                  action={(
                    <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={resetFilters}>
                      {t('promo.clearFilters')}
                    </button>
                  )}
                />
              </div>
            ) : (
              <div
                ref={scroller.ref}
                className={`ui-table-scroll pc-scroll ${scroller.fade}`.trim()}
                role="region"
                aria-label={t('promo.title')}
                tabIndex={0}
              >
                <table className="ui-table ui-table--stack pc-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('promo.colCode')}</th>
                      <th scope="col">{t('promo.colDiscount')}</th>
                      <th scope="col" className="pc-th-uses">{t('promo.colUsage')}</th>
                      <th scope="col">{t('promo.colValidity')}</th>
                      <th scope="col">{t('promo.colStatus')}</th>
                      <th scope="col"><span className="visually-hidden">{t('promo.colActions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(({ pc, state }) => {
                      const used = pc.usedCount || 0;
                      const pct = pc.maxUses ? Math.min(100, Math.round((used / pc.maxUses) * 100)) : 0;
                      return (
                        <tr key={pc.id}>
                          <td className="pc-col-code">
                            <div className="pc-code-cell">
                              <button type="button" className="pc-code" onClick={() => copyCode(pc.code)} title={t('promo.copyCodeTitle')} aria-label={`${t('promo.copyCodeTitle')}: ${pc.code}`}>
                                <span>{pc.code}</span>
                                <Copy aria-hidden="true" />
                              </button>
                              {pc.name && <span className="pc-code-name">{pc.name}</span>}
                            </div>
                          </td>
                          <td className="pc-col-discount" data-label={t('promo.colDiscount')}>
                            <span className="pc-discount">{discountLabel(pc)}</span>
                            <span className="pc-scope" title={scopeTitle(pc)}>{scopeLabel(pc)}</span>
                          </td>
                          <td className="pc-col-usage" data-label={t('promo.colUsage')}>
                            <div className="pc-uses">
                              <span className="pc-uses-text">{formatNumber(used, { lang })}{pc.maxUses ? ` / ${formatNumber(pc.maxUses, { lang })}` : ''}</span>
                              {pc.maxUses ? (
                                <ProgressBar
                                  className="pc-uses-bar"
                                  value={pct}
                                  size="sm"
                                  tone={pct >= 100 ? 'warning' : undefined}
                                  complete={false}
                                  label={`${t('promo.colUsage')}: ${pc.code}`}
                                />
                              ) : <span className="pc-uses-inf">{t('promo.unlimited')}</span>}
                            </div>
                          </td>
                          <td className="pc-col-valid" data-label={t('promo.colValidity')}>
                            <span className="pc-validity"><DateRange text={validityLabel(pc)} /></span>
                          </td>
                          <td className="pc-col-state" data-label={t('promo.colStatus')}>
                            <div className="pc-status">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(pc.isActive)}
                                aria-label={`${pc.isActive ? t('promo.deactivate') : t('promo.activate')}: ${pc.code}`}
                                className="ui-switch hit-44"
                                onClick={() => handleToggle(pc)}
                                title={pc.isActive ? t('promo.deactivate') : t('promo.activate')}
                              />
                              <StatusBadge tone={state.tone}>{t(`promo.${state.key}`)}</StatusBadge>
                            </div>
                          </td>
                          <td className="pc-col-actions ui-row-actions-cell">
                            <div className="ui-row-actions">
                              <button type="button" className="ui-icon-btn" title={t('promo.editTitle')} aria-label={`${t('promo.editTitle')}: ${pc.code}`} onClick={() => openEdit(pc)}>
                                <Pencil aria-hidden="true" />
                              </button>
                              <button type="button" className="ui-icon-btn ui-icon-btn--danger" title={t('promo.deleteTitle')} aria-label={`${t('promo.deleteTitle')}: ${pc.code}`} onClick={() => setConfirmDel(pc)}>
                                <Trash2 aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Create / edit dialog */}
      {modal && (
        <Modal
          open
          as="form"
          onSubmit={handleSave}
          onClose={() => { if (!saving) setModal(null); }}
          closeOnBackdrop={!dirty && !saving}
          title={modal.mode === 'edit' ? t('promo.modalEditTitle') : t('promo.modalNewTitle')}
          size="md"
          footer={(
            <>
              {/* "Active" is the one switch of the form: it lives beside the buttons, so the form itself fits the dialog */}
              <label className="pc-active-toggle" title={t('promo.activeRowLabel')}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(form.isActive)}
                  aria-labelledby="pc-active-label"
                  className="ui-switch"
                  onClick={() => setField('isActive', !form.isActive)}
                />
                <span id="pc-active-label">{t('promo.filterActive')}</span>
              </label>
              <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setModal(null)} disabled={saving}>{t('promo.cancel')}</button>
              <button type="submit" className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`} disabled={saving}>
                {modal.mode === 'edit' ? t('promo.saveChanges') : t('promo.createPromocode')}
              </button>
            </>
          )}
        >
          <div className="ui-form-grid">
            <div className="ui-field is-wide">
              <label className="ui-label" htmlFor="pc-code">{t('promo.codeLabel')}<span className="ui-req" aria-hidden="true">*</span></label>
              <div className="pc-code-input">
                <input
                  id="pc-code"
                  className="pc-code-field"
                  type="text"
                  value={form.code}
                  maxLength={24}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={errors.code ? 'true' : undefined}
                  aria-describedby={errors.code ? 'pc-code-error' : undefined}
                  onChange={(e) => setField('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  placeholder={t('promo.codePlaceholder')}
                />
                <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setField('code', genCode())}>
                  <Dices aria-hidden="true" />
                  {t('promo.generate')}
                </button>
              </div>
              {errors.code && <p className="ui-error" id="pc-code-error" role="alert">{errors.code}</p>}
            </div>

            <div className="ui-field is-wide">
              <label className="ui-label" htmlFor="pc-name">{t('promo.nameLabel')} <span className="pc-optional">{t('promo.optional')}</span></label>
              <input id="pc-name" type="text" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder={t('promo.namePlaceholder')} maxLength={80} />
            </div>

            <div className="ui-field">
              <span className="ui-label" id="pc-type-label">{t('promo.discountTypeLabel')}</span>
              <Dropdown
                value={form.type}
                onChange={(v) => setField('type', v)}
                labelledBy="pc-type-label"
                options={[
                  { value: 'PERCENTAGE', label: t('promo.percentageOption') },
                  { value: 'FIXED_AMOUNT', label: t('promo.fixedAmountOption') },
                ]}
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="pc-value">{form.type === 'PERCENTAGE' ? t('promo.percentOffLabel') : t('promo.amountOffLabelUZS')}<span className="ui-req" aria-hidden="true">*</span></label>
              <input
                id="pc-value"
                type="number"
                inputMode="decimal"
                min="0"
                max={form.type === 'PERCENTAGE' ? 100 : undefined}
                value={form.value}
                aria-invalid={errors.value ? 'true' : undefined}
                aria-describedby={errors.value ? 'pc-value-error' : undefined}
                onChange={(e) => setField('value', e.target.value)}
                placeholder={form.type === 'PERCENTAGE' ? '25' : '50000'}
              />
              {errors.value && <p className="ui-error" id="pc-value-error" role="alert">{errors.value}</p>}
            </div>

            {/* Applies to: every course, or a chosen few */}
            <div className="ui-field is-wide">
              <span className="ui-label">{t('promo.appliesToLabel')}</span>
              <SubTabs
                className="pc-scope-tabs"
                tabs={[
                  { key: 'GLOBAL', label: t('promo.allCoursesTitle') },
                  { key: 'COURSE', label: t('promo.specificCoursesTitle') },
                ]}
                active={form.scope}
                onChange={(v) => setField('scope', v)}
                ariaLabel={t('promo.appliesToLabel')}
              />
              {form.scope === 'COURSE' && (
                <>
                  <p className="ui-hint">{t('promo.specificCoursesDesc')}</p>
                  <div className="pc-courses" id="pc-courses" tabIndex={-1} aria-invalid={errors.scope ? 'true' : undefined}>
                    {courses.length === 0 ? (
                      <p className="pc-courses-empty">{t('promo.noCoursesFound')}</p>
                    ) : (
                      <>
                        <div className="pc-courses-bar">
                          <span>{t('promo.coursesSelected', { selected: form.courseIds.length, total: courses.length })}</span>
                          {form.courseIds.length > 0 && (
                            <button type="button" className="pc-courses-clear" onClick={() => setField('courseIds', [])}>{t('promo.clear')}</button>
                          )}
                        </div>
                        <div className="pc-courses-list">
                          {courses.map((c) => (
                            <label key={c.id} className="pc-course-row ui-check">
                              <input type="checkbox" checked={form.courseIds.includes(c.id)} onChange={() => toggleCourse(c.id)} />
                              <span className="pc-course-title">{c.title}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
              {errors.scope && <p className="ui-error" role="alert">{errors.scope}</p>}
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="pc-starts">{t('promo.startsLabel')} <span className="pc-optional">{t('promo.optional')}</span></label>
              <DatePicker
                id="pc-starts"
                value={form.startsAt}
                onChange={(v) => setField('startsAt', v)}
                max={form.endsAt || undefined}
                invalid={Boolean(errors.dates)}
                aria-label={t('promo.startsLabel')}
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="pc-ends">{t('promo.expiresLabel')} <span className="pc-optional">{t('promo.optional')}</span></label>
              <DatePicker
                id="pc-ends"
                value={form.endsAt}
                onChange={(v) => setField('endsAt', v)}
                min={form.startsAt || undefined}
                invalid={Boolean(errors.dates)}
                aria-label={t('promo.expiresLabel')}
              />
            </div>
            {errors.dates && <p className="ui-error is-wide" role="alert">{errors.dates}</p>}
            <div className="ui-chip-row ui-chip-row--wrap is-wide pc-durations" role="group" aria-label={t('promo.durationAria')}>
              {DURATIONS.map((days) => (
                <button
                  key={days}
                  type="button"
                  className="ui-chip"
                  aria-pressed={Boolean(form.startsAt) && form.endsAt === shiftYmd(form.startsAt, days - 1)}
                  onClick={() => applyDuration(days)}
                >
                  {t('promo.durationDays', { count: days })}
                </button>
              ))}
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="pc-max">{t('promo.maxUsesLabel')}</label>
              <input
                id="pc-max"
                type="number"
                inputMode="numeric"
                min="1"
                value={form.maxUses}
                aria-invalid={errors.limits ? 'true' : undefined}
                onChange={(e) => setField('maxUses', e.target.value)}
                placeholder="100"
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="pc-max-user">{t('promo.maxPerStudentLabel')}</label>
              <input
                id="pc-max-user"
                type="number"
                inputMode="numeric"
                min="1"
                value={form.maxUsesPerUser}
                aria-invalid={errors.limits ? 'true' : undefined}
                onChange={(e) => setField('maxUsesPerUser', e.target.value)}
                placeholder="1"
              />
            </div>
            {errors.limits
              ? <p className="ui-error is-wide" role="alert">{errors.limits}</p>
              : <p className="ui-hint is-wide pc-limits-hint">{t('promo.maxUsesHint')}</p>}

            <div className="ui-field is-wide">
              <label className="ui-label" htmlFor="pc-min">{t('promo.minOrderLabel')} <span className="pc-optional">{t('promo.optional')}</span></label>
              <input id="pc-min" type="number" inputMode="numeric" min="0" value={form.minOrderAmount} onChange={(e) => setField('minOrderAmount', e.target.value)} placeholder="0" />
            </div>

            {formErr && (
              <div className="ui-alert is-wide" role="alert" ref={formErrRef}>
                <p>{formErr}</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Delete confirmation: the question, one line about what follows */}
      <ConfirmDialog
        open={Boolean(confirmDel)}
        title={t('promo.deleteConfirmTitle')}
        description={confirmDel ? t('promo.deleteConfirmMsg', { code: confirmDel.code }) : undefined}
        confirmLabel={t('promo.deleteTitle')}
        cancelLabel={t('promo.cancel')}
        tone="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}

export default Promocodes;
